import React, { useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import {
  FaGraduationCap, FaChartBar, FaMedal, FaPlusCircle, FaInbox, FaTrash, FaEdit, FaFilter, FaSearch
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { authFetch } from '../utils/api';
import useRolePermissions, { hasPermission } from '../hooks/useRolePermissions';

function useDebounce(value, delay=300){
  const [v, setV] = useState(value);
  useEffect(()=>{ const t=setTimeout(()=>setV(value), delay); return ()=>clearTimeout(t) },[value, delay]);
  return v;
}

export default function Education() {
  /* ------------------------------- User meta ------------------------------ */
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/default-user.png',
  };

  // Add/Update/Delete previously had no gate beyond page access. Fallback
  // is this page's own route access list, so nothing anyone could already
  // do stops working until an admin explicitly narrows it.
  const quizPerm = useRolePermissions(user.role);
  const canManageQuiz = hasPermission('education_quiz_manage', quizPerm, user.role, ['System Admin', 'president', 'Vice President']);

  /* ------------------------------ UI toasts ------------------------------- */
  const [toast, setToast] = useState(null);
  const showToast = (msg, tone='success') => {
    setToast({ msg, tone });
    setTimeout(()=>setToast(null), 2500);
  };

  /* ------------------------------ CRUD state ------------------------------ */
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  /* --------------------------- Create form state -------------------------- */
  const [quizDisabled, setQuizDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    subject: '',
    quiz: '',
    question: '',
    option1: '',
    option2: '',
    option3: '',
    option4: '',
    correct_option: '1',
    explanation: ''
  });

  /* ----------------------------- List + filter ---------------------------- */
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [questionsError, setQuestionsError] = useState('');

  const [filterSubject, setFilterSubject] = useState('');
  const [filterQuiz, setFilterQuiz] = useState('');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText, 400);

  // simple client-side pagination
  const [page, setPage] = useState(1);
  const pageSize = 8;

  const fetchQuestions = async () => {
    setQuestionsLoading(true);
    setQuestionsError('');
    try {
      const res = await fetch('https://dev.sliitleo.org/Backend/get_questions.php');
      const data = await res.json();
      if (data?.success && Array.isArray(data.questions)) {
        setQuestions(data.questions);
      } else {
        setQuestionsError(data?.error || 'Failed to load questions.');
      }
    } catch {
      setQuestionsError('Network error.');
    } finally {
      setQuestionsLoading(false);
    }
  };
  useEffect(() => { fetchQuestions(); }, []);

  /* ----------------------------- Derived lists ---------------------------- */
  const subjects = useMemo(() => [...new Set(questions.map(q => q.subject))], [questions]);
  const quizzes = useMemo(() => [...new Set(questions.map(q => q.quiz))], [questions]);

  const filtered = useMemo(() => {
    const base = questions.filter(q =>
      (filterSubject === '' || q.subject === filterSubject) &&
      (filterQuiz === '' || q.quiz === filterQuiz) &&
      (debouncedSearch.trim() === '' ||
        q.question.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        [q.option1, q.option2, q.option3, q.option4].some(o => (o||'').toLowerCase().includes(debouncedSearch.toLowerCase())))
    );
    // reset page if filters change and page overflows
    if ((page-1)*pageSize >= base.length && page !== 1) setPage(1);
    return base;
  }, [questions, filterSubject, filterQuiz, debouncedSearch]); // eslint-disable-line

  const paged = useMemo(() => filtered.slice((page-1)*pageSize, page*pageSize), [filtered, page]);

  /* ------------------------------- Handlers ------------------------------- */
  function handleChange(e){
    const { name, value } = e.target;
    if (name === 'subject'){
      if (value === 'Club History'){
        setFormData(p => ({...p, subject:value, quiz:'Quiz 1: Milestones of Leo Club of SLIIT'}));
        setQuizDisabled(true);
      } else if (value === 'Leo History'){
        setFormData(p => ({...p, subject:value, quiz:'Quiz 2: The Journey of Leoism Worldwide'}));
        setQuizDisabled(true);
      } else if (value === 'Lions International'){
        setFormData(p => ({...p, subject:value, quiz:'Quiz 3: How Lions Clubs Changed the World'}));
        setQuizDisabled(true);
      } else {
        setFormData(p => ({...p, subject:value, quiz:''}));
        setQuizDisabled(false);
      }
    } else {
      setFormData(p => ({...p, [name]: value}));
    }
  }

  async function handleSubmit(e){
    e.preventDefault();
    setLoading(true);
    setSuccess(''); setError('');
    try{
      const res = await authFetch('https://dev.sliitleo.org/Backend/add_question.php', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data?.success){
        setSuccess(data.message || 'Question added!');
        showToast('Question added', 'success');
        setFormData({
          subject:'', quiz:'', question:'', option1:'', option2:'', option3:'', option4:'', correct_option:'1', explanation:''
        });
        setQuizDisabled(false);
        fetchQuestions();
      } else {
        setError(data?.error || 'Something went wrong.');
        showToast('Add failed', 'error');
      }
    } catch {
      setError('Network error. Please try again.');
      showToast('Network error', 'error');
    } finally {
      setLoading(false);
    }
  }

  function handleEditQuestion(q){
    setEditData(q);
    setActionError('');
    setEditModalOpen(true);
  }

  async function handleUpdateQuestion(e){
    e.preventDefault();
    if (!editData?.id) return;
    setActionLoading(true);
    setActionError('');
    try{
      const res = await authFetch('https://dev.sliitleo.org/Backend/update_question.php', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify(editData)
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.error || 'Update failed');
      setEditModalOpen(false);
      showToast('Question updated', 'success');
      fetchQuestions();
    } catch (err){
      setActionError(err.message);
      showToast('Update failed', 'error');
    } finally {
      setActionLoading(false);
    }
  }

  function handleDeleteQuestion(id){
    setActionError('');
    setDeleteModal({ open:true, id });
  }

  async function confirmDeleteQuestion(){
    if (!deleteModal.id) return;
    setActionLoading(true);
    setActionError('');
    try{
      const res = await authFetch('https://dev.sliitleo.org/Backend/delete_question.php', {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify({ id: deleteModal.id })
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.error || 'Delete failed');
      setDeleteModal({ open:false, id:null });
      showToast('Question deleted', 'success');
      fetchQuestions();
    } catch (err){
      setActionError(err.message);
      showToast('Delete failed', 'error');
    } finally {
      setActionLoading(false);
    }
  }

  /* ------------------------------ Modal helpers --------------------------- */
  const editFirstFieldRef = useRef(null);
  useEffect(() => {
    if (editModalOpen && editFirstFieldRef.current){
      editFirstFieldRef.current.focus();
    }
    const onKey = (e) => {
      if (e.key === 'Escape'){
        if (editModalOpen) setEditModalOpen(false);
        if (deleteModal.open) setDeleteModal({ open:false, id:null });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editModalOpen, deleteModal.open]);

  /* --------------------------------- JSX --------------------------------- */
  return (
    <div className="min-h-screen">
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        {/* Header */}
        <div className="page-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}>
            <span style={{
              width: 44, height: 44, borderRadius: 'var(--radius-md)', flexShrink: 0,
              display: 'grid', placeItems: 'center', background: 'var(--accent)', color: '#fff',
            }}>
              <FaGraduationCap size={20} />
            </span>
            <div>
              <h1>Education Admin <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>/ Quiz Question</span></h1>
              <p className="page-desc">Author quiz questions, manage answer options, and review existing questions.</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/education-statistics" className="btn btn-ghost btn-sm">
              <FaChartBar size={13} /> Statistics
            </Link>
            <Link to="/education-leaderboard" className="btn btn-ghost btn-sm">
              <FaMedal size={13} /> Leaderboard
            </Link>
            {canManageQuiz && (
            <a href="#add-form" className="btn btn-primary btn-sm">
              <FaPlusCircle size={13} /> Add Quiz
            </a>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="panel" style={{ padding: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="field" style={{ flex: 1, marginBottom: 0 }}>
              <label className="field-label" htmlFor="q-search">Search</label>
              <div style={{ position: 'relative' }}>
                <FaSearch size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                <input
                  id="q-search"
                  type="text"
                  value={searchText}
                  onChange={(e)=>setSearchText(e.target.value)}
                  placeholder="Search questions or options…"
                  style={{ paddingLeft: 34 }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" style={{ flex: 1 }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="f-subject">Subject</label>
                <select
                  id="f-subject"
                  value={filterSubject}
                  onChange={e=>setFilterSubject(e.target.value)}
                >
                  <option value="">All</option>
                  {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="f-quiz">Quiz</label>
                <select
                  id="f-quiz"
                  value={filterQuiz}
                  onChange={e=>setFilterQuiz(e.target.value)}
                >
                  <option value="">All</option>
                  {quizzes.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
              <button
                onClick={()=>{ setFilterSubject(''); setFilterQuiz(''); setSearchText(''); }}
                className="btn btn-ghost"
                style={{ alignSelf: 'end' }}
                title="Clear filters"
              >
                <FaFilter size={13} /> Clear
              </button>
            </div>
          </div>
        </div>

        {/* Add New Question */}
        {canManageQuiz && (
        <div id="add-form" className="panel" style={{ padding: 'var(--sp-5)', marginBottom: 'var(--sp-6)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 'var(--sp-4)' }}>Add New Quiz Question</h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
              <div className="field">
                <label className="field-label" htmlFor="add-subject">Subject</label>
                <select
                  id="add-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Subject</option>
                  <option value="Club History">Club History</option>
                  <option value="Leo History">Leo History</option>
                  <option value="Lions International">Lions International</option>
                </select>
              </div>
              <div className="field">
                <label className="field-label" htmlFor="add-quiz">Quiz Name</label>
                <select
                  id="add-quiz"
                  name="quiz"
                  value={formData.quiz}
                  onChange={handleChange}
                  required
                  disabled={quizDisabled}
                >
                  <option value="">Select Quiz</option>
                  <option value="Quiz 1: Milestones of Leo Club of SLIIT">Quiz 1: Milestones of Leo Club of SLIIT</option>
                  <option value="Quiz 2: The Journey of Leoism Worldwide">Quiz 2: The Journey of Leoism Worldwide</option>
                  <option value="Quiz 3: How Lions Clubs Changed the World">Quiz 3: How Lions Clubs Changed the World</option>
                </select>
              </div>

              <div className="field md:col-span-2">
                <label className="field-label" htmlFor="add-question">Question</label>
                <input
                  id="add-question"
                  type="text"
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <fieldset style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 'var(--sp-4)', marginBottom: 'var(--sp-4)' }}>
              <legend className="eyebrow" style={{ padding: '0 6px' }}>Answer Options</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
                {[1,2,3,4].map(num=>(
                  <div key={num} className="field">
                    <label className="field-label" htmlFor={`add-option${num}`}>Option {num}</label>
                    <input
                      id={`add-option${num}`}
                      type="text"
                      name={`option${num}`}
                      value={formData[`option${num}`]}
                      onChange={handleChange}
                      required
                    />
                  </div>
                ))}
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="field-label" htmlFor="add-correct">Correct Option</label>
                <select
                  id="add-correct"
                  name="correct_option"
                  value={formData.correct_option}
                  onChange={handleChange}
                  required
                >
                  {[1,2,3,4].map(num => <option key={num} value={num}>{`Option ${num}`}</option>)}
                </select>
              </div>
            </fieldset>

            <div className="field">
              <label className="field-label" htmlFor="add-explanation">
                Explanation <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span>
              </label>
              <textarea
                id="add-explanation"
                name="explanation"
                value={formData.explanation}
                onChange={handleChange}
                rows={2}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading && <span className="spinner" />}
                {loading ? 'Adding…' : 'Add Question'}
              </button>
              {success && <div className="success-banner">{success}</div>}
              {error && <div className="error-banner">{error}</div>}
            </div>
          </form>
        </div>
        )}

        {/* Questions list */}
        <div className="panel" style={{ padding: 'var(--sp-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--sp-4)', flexWrap: 'wrap', gap: 8 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>Questions</h2>
            {!questionsLoading && !questionsError && (
              <span className="eyebrow">{filtered.length} total</span>
            )}
          </div>

          {questionsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
              {Array.from({length:5}).map((_,i)=>(
                <div key={i} style={{ padding: 'var(--sp-4)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
                  <div className="skeleton" style={{ height: 12, width: 120, marginBottom: 10 }} />
                  <div className="skeleton" style={{ height: 14, width: '70%', marginBottom: 10 }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div className="skeleton" style={{ height: 22, width: 80 }} />
                    <div className="skeleton" style={{ height: 22, width: 80 }} />
                    <div className="skeleton" style={{ height: 22, width: 80 }} />
                    <div className="skeleton" style={{ height: 22, width: 80 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : questionsError ? (
            <div className="error-banner">{questionsError}</div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><FaInbox size={18} /></div>
              <p>No questions match the selected filters</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                {paged.map(q=>{
                  const options = [q.option1, q.option2, q.option3, q.option4];
                  return (
                    <div key={q.id} style={{ padding: 'var(--sp-4)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface-2)' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                          <span className="pill pill-info"><span className="dot" />{q.subject}</span>
                          <span className="eyebrow truncate" title={q.quiz} style={{ maxWidth: 240 }}>{q.quiz}</span>
                        </div>
                        {canManageQuiz && (
                        <div className="row-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={()=>handleEditQuestion(q)}
                            title="Edit"
                            aria-label={`Edit question ${q.id}`}
                          >
                            <FaEdit size={13} />
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            onClick={()=>handleDeleteQuestion(q.id)}
                            title="Delete"
                            aria-label={`Delete question ${q.id}`}
                            style={{ color: 'var(--error)' }}
                          >
                            <FaTrash size={13} />
                          </button>
                        </div>
                        )}
                      </div>

                      <p style={{ marginTop: 10, fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>{q.question}</p>

                      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {options.map((opt, idx) => {
                          const isCorrect = parseInt(q.correct_option,10) === idx+1;
                          return (
                            <span
                              key={idx}
                              className={isCorrect ? 'pill pill-success' : 'chip'}
                            >
                              {isCorrect && <span className="dot" />}
                              {String.fromCharCode(65+idx)}. {opt}
                            </span>
                          );
                        })}
                      </div>

                      {q.explanation && (
                        <p style={{ marginTop: 8, fontSize: 12, color: 'var(--text-3)' }}>{q.explanation}</p>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--sp-5)', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                  Showing {(page-1)*pageSize + 1}
                  {"–"}
                  {Math.min(page*pageSize, filtered.length)} of {filtered.length}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={()=>setPage(p=>Math.max(1,p-1))}
                    disabled={page===1}
                  >
                    Prev
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={()=>setPage(p=> (p*pageSize<filtered.length ? p+1 : p))}
                    disabled={page*pageSize>=filtered.length}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Edit Modal */}
        {editModalOpen && (
          <div className="modal-overlay">
            <form
              onSubmit={handleUpdateQuestion}
              className="modal-panel panel lg"
              role="dialog" aria-modal="true" aria-labelledby="edit-question-title"
            >
              <div className="modal-head">
                <h3 id="edit-question-title">Update Question</h3>
                <button
                  type="button"
                  className="icon-btn"
                  onClick={()=>setEditModalOpen(false)}
                  aria-label="Close edit dialog"
                  title="Close"
                >
                  ×
                </button>
              </div>

              <div className="modal-body">
                {actionError && <div className="error-banner" style={{ marginBottom: 'var(--sp-4)' }}>{actionError}</div>}

                <div className="field">
                  <label className="field-label" htmlFor="edit-subject">Subject</label>
                  <input
                    id="edit-subject"
                    ref={editFirstFieldRef}
                    type="text"
                    value={editData?.subject || ''}
                    onChange={e => setEditData(d => ({ ...d, subject: e.target.value }))}
                    required
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="edit-quiz">Quiz</label>
                  <input
                    id="edit-quiz"
                    type="text"
                    value={editData?.quiz || ''}
                    onChange={e => setEditData(d => ({ ...d, quiz: e.target.value }))}
                    required
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="edit-question">Question</label>
                  <textarea
                    id="edit-question"
                    value={editData?.question || ''}
                    onChange={e => setEditData(d => ({ ...d, question: e.target.value }))}
                    required
                  />
                </div>

                <fieldset style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 'var(--sp-4)', marginBottom: 'var(--sp-4)' }}>
                  <legend className="eyebrow" style={{ padding: '0 6px' }}>Answer Options</legend>
                  {[1,2,3,4].map(i=>(
                    <div key={i} className="field">
                      <label className="field-label" htmlFor={`edit-option${i}`}>Option {i}</label>
                      <input
                        id={`edit-option${i}`}
                        type="text"
                        value={editData?.[`option${i}`] || ''}
                        onChange={e => setEditData(d => ({ ...d, [`option${i}`]: e.target.value }))}
                        required
                      />
                    </div>
                  ))}
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label className="field-label" htmlFor="edit-correct">Correct Option</label>
                    <select
                      id="edit-correct"
                      value={editData?.correct_option || '1'}
                      onChange={e => setEditData(d => ({ ...d, correct_option: e.target.value }))}
                      required
                    >
                      {[1,2,3,4].map(i => <option key={i} value={i}>{`Correct Option ${i}`}</option>)}
                    </select>
                  </div>
                </fieldset>

                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="field-label" htmlFor="edit-explanation">
                    Explanation <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <textarea
                    id="edit-explanation"
                    value={editData?.explanation || ''}
                    onChange={e => setEditData(d => ({ ...d, explanation: e.target.value }))}
                  />
                </div>
              </div>

              <div className="modal-foot">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={()=>setEditModalOpen(false)}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Updating…' : 'Update Question'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Delete Modal */}
        {deleteModal.open && (
          <div className="modal-overlay">
            <div className="modal-panel panel" role="dialog" aria-modal="true" aria-labelledby="delete-question-title">
              <div className="modal-head">
                <h3 id="delete-question-title">Delete Question?</h3>
              </div>
              <div className="modal-body">
                <p style={{ color: 'var(--text-2)', fontSize: 13, margin: 0 }}>
                  Are you sure you want to delete this question? This action cannot be undone.
                </p>
                {actionError && <div className="error-banner" style={{ marginTop: 'var(--sp-3)' }}>{actionError}</div>}
              </div>
              <div className="modal-foot">
                <button
                  className="btn btn-ghost"
                  onClick={()=>setDeleteModal({ open:false, id:null })}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={confirmDeleteQuestion}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1000 }}>
            <div className={toast.tone === 'success' ? 'success-banner' : 'error-banner'}>
              {toast.msg}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
